import React, { useState, useRef, useEffect, useId, useInsertionEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ensureOverlayStyles, overlayIdentity } from './portalOverlay';
import { useTopLayer } from './topLayer';

// Split after each delimiter so long values (URLs, dotted paths) wrap at "/" or "."
// instead of mid-token. <wbr> is copy-safe (a zero-width space would corrupt a copied
// URL); overflow-wrap still handles a single run too long to fit.
const WRAP_AFTER = /(?<=[/\\.\-_?&#=:@])/;
const withBreakOpportunities = (value: string): React.ReactNode =>
    value.split(WRAP_AFTER).map((chunk, i) => (
        <React.Fragment key={i}>
            {chunk}
            <wbr />
        </React.Fragment>
    ));

interface TooltipProps {
    text: React.ReactNode | string;
    children: React.ReactNode;
    delay?: number;
    wrapChildren?: boolean;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    isShrinkable?: boolean;
    fill?: boolean;
    /** Max width of tooltip. Default: 320px */
    maxWidth?: number | string;
    /** Called synchronously when the trigger is hovered/focused; return false to
     *  suppress the tooltip for this interaction. Lets wrappers defer expensive
     *  checks (e.g. truncation measurement) to interaction time instead of mount. */
    shouldShow?: () => boolean;
    triggerAs?: 'div' | 'span';
    /** Whether the tooltip acts as the trigger's accessible description. False where the
     *  trigger already points at the same sentence on screen, so it is not announced twice. */
    describesTrigger?: boolean;
}

const SHRINKABLE_STYLE: React.CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
};

const FILL_STYLE: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
};

/** Kept clear of the viewport edge so a clamped tooltip does not sit flush against it. */
const VIEWPORT_MARGIN = 8;

const Tooltip: React.FC<TooltipProps> = ({
    text,
    children,
    delay = 1,
    wrapChildren = false,
    triggerAs = 'div',
    placement = 'top',
    isShrinkable = false,
    maxWidth = '320px',
    fill = false,
    shouldShow,
    describesTrigger = true,
}) => {
    const triggerStyle =
        isShrinkable || fill
            ? { ...(isShrinkable ? SHRINKABLE_STYLE : {}), ...(fill ? FILL_STYLE : {}) }
            : undefined;

    const [isVisible, setIsVisible] = useState(false);
    // Portal mounts on first show, not on mount: an at-rest tooltip otherwise
    // costs a hidden document.body node plus a getBoundingClientRect per render —
    // multiplied by every row in a large table.
    const [hasShown, setHasShown] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const showFrameRef = useRef<number | null>(null);

    const cancelPendingShow = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (showFrameRef.current !== null) {
            cancelAnimationFrame(showFrameRef.current);
            showFrameRef.current = null;
        }
    }, []);

    const scheduleVisible = () => {
        // Two steps so the portal mounts hidden first and the fade-in transition
        // still runs on the very first show. The frame is tracked so leave/blur/
        // unmount can cancel a show that is already past its delay.
        setHasShown(true);
        if (showFrameRef.current !== null) {
            cancelAnimationFrame(showFrameRef.current);
        }
        showFrameRef.current = requestAnimationFrame(() => {
            showFrameRef.current = null;
            setIsVisible(true);
        });
    };
    const tooltipRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    // Tied to visibility, not mount: the node is latched into the DOM after its first show, and
    // re-entering on each show keeps a tooltip above the panel it is annotating.
    useTopLayer(tooltipRef, isVisible);

    const tooltipId = useId();

    const hasText = text !== null && text !== undefined && text !== '' && text !== false;

    const handleMouseEnter = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!hasText || (shouldShow && !shouldShow())) {
            return;
        }

        cancelPendingShow();

        timeoutRef.current = setTimeout(() => {
            scheduleVisible();
        }, delay * 1000);
    };

    const handleMouseLeave = (e: React.MouseEvent) => {
        e.stopPropagation();

        cancelPendingShow();
        setIsVisible(false);
    };

    const handleFocus = () => {
        if (!hasText || (shouldShow && !shouldShow())) {
            return;
        }

        cancelPendingShow();
        scheduleVisible();
    };

    const handleBlur = () => {
        cancelPendingShow();
        setIsVisible(false);
    };

    const [, forceUpdate] = useState({});

    useEffect(() => {
        // Only a VISIBLE tooltip needs to track scroll/resize; keeping global
        // listeners per mounted instance made every scroll event fan out across
        // every tooltip on the page.
        if (!isVisible) {
            return () => {
                cancelPendingShow();
            };
        }
        const handleUpdate = () => {
            forceUpdate({});
        };

        window.addEventListener('scroll', handleUpdate, true);
        window.addEventListener('resize', handleUpdate);

        return () => {
            cancelPendingShow();
            window.removeEventListener('scroll', handleUpdate, true);
            window.removeEventListener('resize', handleUpdate);
        };
    }, [isVisible, cancelPendingShow]);

    // The first render that mounts the tooltip has no measured width yet, so the clamp below
    // cannot run on it. One extra pass once it is in the DOM gives the clamp its measurement
    // — before the opacity transition finishes, so nothing is seen sliding into place.
    useEffect(() => {
        if (isVisible) {
            forceUpdate({});
        }
    }, [isVisible]);

    const getTooltipStyles = (): React.CSSProperties => {
        if (!triggerRef.current) {
            return { display: 'none' };
        }

        const rect = triggerRef.current.getBoundingClientRect();

        const baseStyles: React.CSSProperties = {
            position: 'fixed',
            display: 'flex',
            width: 'max-content',
            maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
            minWidth: '32px',
            minHeight: '32px',
            padding: '8px 12px',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '12px',
            backgroundColor: 'var(--malachite-tooltip-background, rgb(59, 59, 59))',
            color: 'var(--malachite-tooltip-foreground, rgb(255, 255, 255))',
            boxShadow: 'var(--malachite-tooltip-shadow, 0px 0px 10px 0px rgba(0, 0, 0, 0.20))',
            fontSize: '14px',
            lineHeight: '1.4',
            whiteSpace: 'normal',
            textAlign: 'left',
            pointerEvents: 'none',
            opacity: isVisible ? 1 : 0,
            visibility: isVisible ? 'visible' : 'hidden',
            transition:
                'opacity 150ms ease-in-out, transform 150ms ease-in-out, visibility 150ms ease-in-out',
        };

        // A trigger near the viewport edge would otherwise centre a wide tooltip half off
        // screen, where the panel or the window crops it. Clamping the CENTRE keeps the
        // `translateX(-50%)` placement honest: the box stays put, it just stops short.
        const centeredLeft = (): number => {
            const desired = rect.left + rect.width / 2;
            const width = tooltipRef.current?.getBoundingClientRect().width ?? 0;

            if (width === 0) {
                return desired;
            }

            const half = width / 2;
            const min = VIEWPORT_MARGIN + half;
            const max = window.innerWidth - VIEWPORT_MARGIN - half;

            // A tooltip wider than the viewport has no in-bounds centre; leave it centred
            // rather than pinning it to a nonsensical one.
            return min > max ? desired : Math.min(Math.max(desired, min), max);
        };

        switch (placement) {
            case 'top':
                return {
                    ...baseStyles,
                    top: rect.top - 8,
                    left: centeredLeft(),
                    transform: `translate(-50%, -100%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
                };
            case 'bottom':
                return {
                    ...baseStyles,
                    top: rect.bottom + 8,
                    left: centeredLeft(),
                    transform: `translateX(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
                };
            case 'left':
                return {
                    ...baseStyles,
                    top: rect.top + rect.height / 2,
                    left: rect.left - 8,
                    transform: `translate(-100%, -50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
                };
            case 'right':
                return {
                    ...baseStyles,
                    top: rect.top + rect.height / 2,
                    left: rect.right + 8,
                    transform: `translateY(-50%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
                };
            default:
                return {
                    ...baseStyles,
                    top: rect.top - 8,
                    left: centeredLeft(),
                    transform: `translate(-50%, -100%) ${isVisible ? 'scale(1)' : 'scale(0.95)'}`,
                };
        }
    };

    // Only reference the tooltip id once the portal can exist — a dangling
    // aria-describedby (text set, never shown) confuses assistive tech.
    const describedBy = hasText && hasShown && describesTrigger ? tooltipId : undefined;
    useInsertionEffect(() => {
        if (hasText && hasShown) {
            ensureOverlayStyles();
        }
    }, [hasText, hasShown]);
    const renderedChildren =
        wrapChildren || !React.isValidElement(children) ? (
            <span aria-describedby={describedBy} style={fill ? FILL_STYLE : undefined}>
                {children}
            </span>
        ) : (
            React.cloneElement(children as React.ReactElement, {
                'aria-describedby':
                    [(children as React.ReactElement).props['aria-describedby'], describedBy]
                        .filter(Boolean)
                        .join(' ') || undefined,
            })
        );

    const Trigger = triggerAs;

    // Create the tooltip content without its own shadow root
    return (
        <>
            <Trigger
                ref={triggerRef}
                className="relative flex items-center"
                style={triggerStyle}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                {renderedChildren}
            </Trigger>

            {hasText &&
                hasShown &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        id={tooltipId}
                        role="tooltip"
                        // Nothing references it, and the sentence is already on screen and
                        // described: left in the tree it is a second copy to read past.
                        aria-hidden={describesTrigger ? undefined : true}
                        {...overlayIdentity('tooltip')}
                        style={getTooltipStyles()}
                    >
                        {/* min-w-0 + overflow-wrap let long values wrap within maxWidth. */}
                        <span
                            style={{
                                minWidth: 0,
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {typeof text === 'string' ? withBreakOpportunities(text) : text}
                        </span>
                    </div>,
                    document.body,
                )}
        </>
    );
};

export default Tooltip;
